/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Public profile viewer (senior request 2026-06-15): ANY authenticated user can
 * click a person's name to see their CV, certificates and signature.
 *
 * - <ProfileModal userId onClose /> fetches GET /api/users/:id/profile and renders
 *   the person's summary, signature, CV history (download) and certificates (download).
 * - <ProfileViewerProvider> + useProfileViewer() lets any component open the modal by
 *   user id without threading state: `const { open } = useProfileViewer(); open(userId)`.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { PublicProfileResponse } from '@heros/shared';
import { apiClient } from '../lib/apiClient';
import { useData } from '../context/AppDataContext';
import { computePersonWork } from '../lib/workload';
import { Modal, Avatar, Ico } from './ui';

const thb = (v: number) => '฿' + Math.round(v).toLocaleString();

/** A person's work portfolio (tasks / jobs / projects / money) for the profile,
 *  computed from the in-memory store via the shared workload helper so it matches
 *  the Team Workload tab and Portfolio Overview exactly. */
function ProfileWork({ userId }: { userId: string }) {
  const D = useData();
  const { byUser } = computePersonWork({ timeline: D.timeline, compensations: D.compensations, team: D.team });
  const work = byUser[userId];
  const toneOf = (cat: string) => (D.categories[cat] || { tone: 'slate' }).tone;
  const cats = work ? Object.entries(work.cats).sort((a, b) => b[1] - a[1]) : [];
  const maxCat = Math.max(1, ...cats.map(([, n]) => n));

  return (
    <div className="card">
      <div className="card-h"><h3>Work &amp; contribution</h3><span className="sub">across all projects</span></div>
      <div className="card-b">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: cats.length ? 12 : 0 }}>
          <div className="stat"><div className="label">Task allocations</div><div className="v tabular">{work?.tasks ?? 0}</div></div>
          <div className="stat"><div className="label">Projects</div><div className="v tabular">{work?.projects.size ?? 0}</div></div>
          <div className="stat"><div className="label">Earned</div><div className="v" style={{ color: 'var(--accent)' }}>{thb(work?.earned ?? 0)}</div></div>
          <div className="stat"><div className="label">Received</div><div className="v" style={{ color: 'var(--t-green-fg)' }}>{thb(work?.received ?? 0)}</div></div>
        </div>
        {cats.length ? (
          <div>
            <div className="label" style={{ marginBottom: 6 }}>Jobs by type</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {cats.map(([c, n]) => (
                <div key={c} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ flex: '0 0 150px', fontSize: 11.5, color: `var(--t-${toneOf(c)}-fg)`, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c}</span>
                  <div className="bar-track" style={{ flex: 1 }}><div className="bar-fill" style={{ width: (n / maxCat * 100) + '%', background: `var(--t-${toneOf(c)}-fg)`, opacity: 0.85 }} /></div>
                  <span className="tabular" style={{ width: 26, textAlign: 'right', fontSize: 11.5, fontWeight: 700 }}>{n}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>No task allocations or earnings recorded yet.</div>
        )}
      </div>
    </div>
  );
}

function certExpiryTone(expiry: string): { tone: string; label: string } {
  const d = new Date(expiry + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return { tone: 'slate', label: expiry || '—' };
  const days = Math.round((d.getTime() - Date.now()) / 86400000);
  if (days < 0) return { tone: 'red', label: `expired ${expiry}` };
  if (days < 60) return { tone: 'amber', label: `expires ${expiry}` };
  return { tone: 'green', label: `valid · ${expiry}` };
}

export function ProfileModal({ userId, onClose }: { userId: string; onClose: () => void }) {
  const [data, setData] = useState<PublicProfileResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setData(null); setErr(null);
    apiClient.users.profile(userId)
      .then((d) => { if (live) setData(d); })
      .catch((e) => { if (live) setErr(e?.message || 'Could not load this profile.'); });
    return () => { live = false; };
  }, [userId]);

  const sig = data?.profile?.signature;
  const sigIsImage = !!sig && String(sig).startsWith('data:');

  return (
    <Modal title="Team member profile" onClose={onClose} wide>
      {!data && !err && <div style={{ padding: 20, color: 'var(--text-2)' }}>Loading profile…</div>}
      {err && <div style={{ padding: 20, color: 'var(--accent)' }}>{err}</div>}
      {data && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Avatar user={{ name: data.user.name, initials: data.user.initials } as any} size="lg" />
            <div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{data.user.name}</div>
              <div style={{ fontSize: 12, color: 'var(--text-2)' }}>{data.user.role}{data.profile.position ? ` · ${data.profile.position}` : ''}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{data.user.email}{data.profile.affiliation ? ` · ${data.profile.affiliation}` : ''}</div>
            </div>
          </div>
          {(data.profile.expertise || data.profile.bio) && (
            <div style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.5 }}>
              {data.profile.expertise && <div><strong>Expertise:</strong> {data.profile.expertise}</div>}
              {data.profile.bio && <div style={{ marginTop: 4 }}>{data.profile.bio}</div>}
            </div>
          )}

          {/* Work portfolio — what tasks/jobs they do + their compensation. */}
          <ProfileWork userId={userId} />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {/* CV history */}
            <div className="card">
              <div className="card-h"><h3>CV</h3></div>
              <div className="card-b" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {data.cvHistory.length === 0 ? (
                  <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>No CV uploaded.</div>
                ) : data.cvHistory.map((cv) => (
                  <a key={cv.id} href={apiClient.users.cvFileUrl(userId, cv.id)} target="_blank" rel="noreferrer"
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, fontSize: 12, padding: '4px 0', textDecoration: 'none', color: 'var(--text)' }}>
                    <span><Ico.dl /> {cv.fileName}{cv.current ? ' · current' : ''}</span>
                    <span style={{ fontSize: 10.5, color: 'var(--text-3)' }}>{cv.stampedAt}</span>
                  </a>
                ))}
              </div>
            </div>
            {/* Certificates */}
            <div className="card">
              <div className="card-h"><h3>Certificates</h3></div>
              <div className="card-b" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {data.certificates.length === 0 ? (
                  <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>No certificates.</div>
                ) : data.certificates.map((c) => {
                  const t = certExpiryTone(c.expiry);
                  return (
                    <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, fontSize: 12 }}>
                      <span>
                        {c.fileName
                          ? <a href={apiClient.users.certFileUrl(userId, c.id)} target="_blank" rel="noreferrer" style={{ textDecoration: 'none', color: 'var(--text)' }}><Ico.dl /> {c.name}</a>
                          : c.name}
                      </span>
                      <span className="chip" data-tone={t.tone}><span className="dot" />{t.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Signature */}
          <div className="card">
            <div className="card-h"><h3>Signature</h3></div>
            <div className="card-b">
              {!sig ? (
                <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>No signature on file.</div>
              ) : sigIsImage ? (
                <img src={sig} alt="signature" style={{ maxHeight: 90, maxWidth: '100%', background: '#fff', border: '1px solid var(--border)', borderRadius: 6, padding: 4 }} />
              ) : (
                <div style={{ fontFamily: 'cursive', fontSize: 22 }}>{sig}</div>
              )}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

const ProfileViewerCtx = createContext<{ open: (userId: string) => void }>({ open: () => {} });
export function useProfileViewer() { return useContext(ProfileViewerCtx); }

export function ProfileViewerProvider({ children }: { children: ReactNode }) {
  const [userId, setUserId] = useState<string | null>(null);
  return (
    <ProfileViewerCtx.Provider value={{ open: setUserId }}>
      {children}
      {userId && <ProfileModal userId={userId} onClose={() => setUserId(null)} />}
    </ProfileViewerCtx.Provider>
  );
}
